import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface MenuItem {
  key: string;
  label?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  // "group" renders a non-clickable section header above its children; "divider" a separator.
  type?: "group" | "divider";
  children?: MenuItem[];
}

export interface MenuProps {
  items: MenuItem[];
  selectedKey?: string;
  defaultOpenKeys?: string[];
  onSelect?: (key: string) => void;
  className?: string;
  /** "dark" styles for the app sidebar rail; "light" for on-surface menus. */
  theme?: "light" | "dark";
}

export function Menu({
  items,
  selectedKey,
  defaultOpenKeys,
  onSelect,
  className,
  theme = "light",
}: MenuProps) {
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys ?? []);
  const dark = theme === "dark";

  const base =
    "group relative flex h-10 w-full items-center gap-3 rounded-lg px-3 text-sm outline-none transition-all";
  // The rail states read the --sidebar-accent pair rather than an alpha of the
  // rail's own ink: on the white rail an ink-alpha selection came out muddy gray,
  // disconnected from the indigo left bar right next to it. The tokens flip with
  // the theme, so one rule covers the white rail and the dark one.
  const idle = dark
    ? "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
    : "text-foreground/70 hover:bg-accent hover:text-accent-foreground";
  const active = dark
    ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
    : "bg-accent font-medium text-accent-foreground";

  const renderItem = (item: MenuItem, depth: number): ReactNode => {
    if (item.type === "divider") {
      return <div key={item.key} className={cn("my-2 h-px", dark ? "bg-sidebar-border/60" : "bg-border")} />;
    }
    if (item.type === "group") {
      return (
        <div key={item.key} className="mt-3">
          <div
            className={cn(
              "px-3 pb-1 text-xs font-semibold uppercase tracking-wide",
              dark ? "text-sidebar-foreground/50" : "text-muted-foreground",
            )}
          >
            {item.label}
          </div>
          {item.children?.map((c) => renderItem(c, depth))}
        </div>
      );
    }
    if (item.children && item.children.length > 0) {
      const isOpen = openKeys.includes(item.key);
      return (
        <div key={item.key}>
          <button
            type="button"
            disabled={item.disabled}
            onClick={() =>
              setOpenKeys((p) => (p.includes(item.key) ? p.filter((k) => k !== item.key) : [...p, item.key]))
            }
            className={cn(base, idle, "justify-between disabled:opacity-50")}
          >
            <span className="flex items-center gap-3">
              {item.icon}
              {item.label}
            </span>
            <ChevronDown className={cn("size-4 shrink-0 transition-transform", isOpen && "rotate-180")} />
          </button>
          {isOpen && (
            <div className="mt-1 space-y-1 pl-4">{item.children.map((c) => renderItem(c, depth + 1))}</div>
          )}
        </div>
      );
    }
    const selected = selectedKey === item.key;
    return (
      <button
        key={item.key}
        type="button"
        disabled={item.disabled}
        onClick={() => onSelect?.(item.key)}
        className={cn(base, selected ? active : idle, "disabled:opacity-50")}
      >
        {selected && dark && (
          <span className="absolute top-1/2 left-0 h-6 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-primary" />
        )}
        {item.icon}
        {item.label}
      </button>
    );
  };

  return <nav className={cn("space-y-1 p-2", className)}>{items.map((it) => renderItem(it, 0))}</nav>;
}
